import { atom, useAtomValue } from "jotai";
import { useMemo } from "react";

import { useDragOperation, type ResizeHandle, type SnapGuide } from "@skilitics/drag-drop";

export const canvasHoveredIdAtom = atom<number | undefined>(undefined);
export const canvasLayoutModeAtom = atom(false);
export const canvasIsEditingAtom = atom(false);
export const canvasIsPanningAtom = atom(false);
export const canvasHiddenContainerIdsAtom = atom<Set<number>>(new Set<number>());

export const setCanvasHiddenContainer = atom(null, (get, set, { id, visible }: { id: number; visible: boolean }) => {
  const current = new Set(get(canvasHiddenContainerIdsAtom));

  if (visible) {
    current.delete(id); // container is visible
  } else {
    current.add(id); // container is not visible
  }

  set(canvasHiddenContainerIdsAtom, current);
});

export const isCanvasContainerVisible = (id: number) => atom((get) => !get(canvasHiddenContainerIdsAtom).has(id));

export const useIsNodeHovered = (nodeId: number) =>
  useAtomValue(useMemo(() => atom((get) => get(canvasHoveredIdAtom) === nodeId), [nodeId]));

export const useIsCanvasDragging = () => {
  const { source } = useDragOperation();
  return useMemo(() => !!source, [source]);
};

export type NodeResizeState = {
  nodeId: number;
  handle: ResizeHandle;
  delta: { x: number; y: number };
};

export const canvasNodeResizeAtom = atom<NodeResizeState | undefined>(undefined);

// Derived boolean so wrappers that only care whether a resize is active don't
// re-render on every pointer-move frame (the resize atom holds a fresh object each frame).
export const canvasIsAnyNodeResizingAtom = atom((get) => get(canvasNodeResizeAtom) !== undefined);

// Per-node slice: returns the resize state only for the matching node (else a stable
// undefined), so non-resizing wrappers bail out while the resizing node still gets live state.
export const useNodeResizeState = (nodeId: number) =>
  useAtomValue(
    useMemo(
      () =>
        atom((get) => {
          const state = get(canvasNodeResizeAtom);
          return state?.nodeId === nodeId ? state : undefined;
        }),
      [nodeId],
    ),
  );

export const canvasSnapGuidesAtom = atom<SnapGuide[] | undefined>(undefined);

export type TransformOriginDragState = {
  nodeId: number;
  fractions: { ox: number; oy: number };
};

export const canvasTransformOriginDragAtom = atom<TransformOriginDragState | undefined>(undefined);

// Derived boolean so wrappers that only care whether a drag is active don't
// re-render on every pointer-move frame (the drag atom holds a fresh object each frame).
export const canvasIsTransformOriginDraggingAtom = atom((get) => get(canvasTransformOriginDragAtom) !== undefined);
